import { useTranslation } from "react-i18next";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";
import type { PrintLayoutFormValues } from "./printLayoutForm";

type Layout = PrintLayoutFormValues["headerLayout"];
type Block = { id: "inline" | "center" | "left" | "right" | "full"; labelKey: string };

const BLOCKS: Block[] = [
  { id: "inline", labelKey: "inline" },
  { id: "center", labelKey: "center" },
  { id: "left", labelKey: "imageText" },
  { id: "right", labelKey: "textImage" },
  { id: "full", labelKey: "full" },
];

const Logo = () => <div className="h-3 w-3 rounded-[3px] bg-primary/40" />;
const Lines = ({ align = "start" }: { align?: "start" | "center" }) => (
  <div className={cn("flex flex-1 flex-col gap-[3px]", align === "center" && "items-center")}>
    <div className="h-[3px] w-4/5 rounded-full bg-muted-foreground/60" />
    <div className="h-[3px] w-3/5 rounded-full bg-muted-foreground/30" />
  </div>
);

function Thumb({ id }: { id: Block["id"] }) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        "flex h-[34px] w-full items-center gap-1.5 rounded-[5px] border bg-card px-1.5",
        id === "center" && "flex-col justify-center gap-[3px]",
        id === "full" && "p-0",
      )}
    >
      {id === "inline" ? (
        <>
          <Logo />
          <Lines />
        </>
      ) : null}
      {id === "center" ? (
        <>
          <Logo />
          <Lines align="center" />
        </>
      ) : null}
      {id === "left" ? (
        <>
          <Logo />
          <Lines />
        </>
      ) : null}
      {id === "right" ? (
        <>
          <Lines />
          <Logo />
        </>
      ) : null}
      {id === "full" ? <div className="h-full w-full rounded-[4px] bg-primary/40" /> : null}
    </div>
  );
}

type Props = { value: Layout; onChange: (layout: Layout) => void; labelledBy?: string; variant?: "desktop" | "mobile" };

export function HeaderLayoutPicker({ value, onChange, labelledBy, variant = "desktop" }: Props) {
  const { t } = useTranslation("settings");
  const isInline = value === "inline" || value === "inline_apart";
  return (
    <div className="flex flex-col gap-3">
      <div role="group" aria-labelledby={labelledBy} aria-label={labelledBy ? undefined : t("printEditor.header.layout")} className="grid grid-cols-2 gap-2">
        {BLOCKS.map((block) => {
          const active = block.id === "inline" ? isInline : value === block.id;
          return (
            <button
              key={block.id}
              type="button"
              aria-pressed={active}
              onClick={() => onChange(block.id === "inline" && isInline ? value : block.id)}
              className={cn(
                variant === "mobile" && "min-h-[52px]",
                "flex flex-col gap-1.5 rounded-xl border p-2 text-left text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                active && "border-2 border-primary bg-accent font-semibold",
              )}
            >
              <Thumb id={block.id} />
              <span>{t(`printEditor.header.${block.labelKey}`)}</span>
            </button>
          );
        })}
      </div>
      {isInline ? (
        <div className="flex flex-col gap-1.5">
          <ToggleGroup
            type="single"
            value={value}
            onValueChange={(next) => next && onChange(next as Layout)}
            aria-label={t("printEditor.header.layoutMode")}
            className="justify-start"
          >
            <ToggleGroupItem value="inline" size="sm">
              {t("printEditor.header.together")}
            </ToggleGroupItem>
            <ToggleGroupItem value="inline_apart" size="sm">
              {t("printEditor.header.apart")}
            </ToggleGroupItem>
          </ToggleGroup>
          <p className="text-xs text-muted-foreground">{t("printEditor.header.togetherHint")}</p>
        </div>
      ) : null}
    </div>
  );
}
